import React from "react"
import Layout from "../components/layout"
import * as bookCss from "./modules/livre.module.scss"
import { Helmet } from "react-helmet"
import couverture from "../images/couverture-livre.jpg"

const previewMetadata = {
  humour: {
    label: "Dessins humoristiques",
    order: 1,
  },
  hummourr: {
    label: "Humour et caricature",
    order: 2,
  },
  spectt: {
    label: "Spectacles et théâtre",
    order: 3,
  },
  statuu: {
    label: "Statuettes et sculptures",
    order: 4,
  },
  mussee: {
    label: "Le Musée Grévin",
    order: 5,
  },
  alfgrev: {
    label: "Portrait d'Alfred Grévin",
    order: 6,
  },
}

const buildPreviewPages = () => {
  const previewContext = require.context(
    "../images/livre-preview",
    false,
    /\.(png|jpe?g|webp)$/i
  )

  return previewContext
    .keys()
    .map(key => {
      const image = previewContext(key)
      const basename = key.replace("./", "").replace(/\.[^.]+$/, "")
      const normalizedBasename = basename.toLowerCase()
      const metadata = previewMetadata[normalizedBasename]

      return {
        basename,
        src: image.default || image,
        alt: `Aperçu du livre d'Alfred Grévin, ${metadata?.label || basename}`,
        label: metadata?.label || null,
        order: metadata?.order || Number.MAX_SAFE_INTEGER,
      }
    })
    .sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order
      }

      return a.basename.localeCompare(b.basename, undefined, { numeric: true })
    })
    .map((page, index) => {
      return {
        ...page,
        label: page.label || `Aperçu ${index + 1}`,
      }
    })
}

const features = [
  {
    title: "Dessins humoristiques",
    text:
      "Retrouvez l'esprit vif et le trait incisif d'Alfred Grévin à travers une sélection d'images qui montrent l'étendue de son talent.",
  },
  {
    title: "Costumes de théâtre",
    text:
      "Découvrez ses projets de costumes et le dialogue constant qu'il entretenait avec la scène, le spectacle et l'art de la mise en image.",
  },
  {
    title: "Illustrations et statuettes",
    text:
      "Le livre montre ses travaux en volume, des créations des plus singulières.",
  },
  {
    title: "Musée Grévin",
    text:
      "Une place importante est accordée à ses créations pour le Musée Grévin, qui prolongent son imaginaire bien au-delà du dessin de presse.",
  },
]

const readers = [
  "Les amateurs d'histoire de l'art et d'artistes du XIXe siècle",
  "Les passionnés d'illustration, de caricature et de presse satirique",
  "Les curieux du théâtre, du costume et des arts du spectacle",
  "Tous ceux qui souhaitent (re)découvrir Alfred Grévin dans toute sa diversité",
]

const locations = [
  {
    name: "Librairie MAIPIU",
    address: ["14 rue de l'hôtel de ville", "89700 Tonnerre"],
    detail: "Téléphone : 06 13 16 32 81",
    href: "tel:+33613163281",
    linkLabel: "Appeler la librairie",
  },
  {
    name: "Office du tourisme du Tonnerrois",
    address: ["61 rue de l'hôpital", "89700 Tonnerre"],
    detail: "Disponible sur place",
  },
  {
    name: "Boutique du Musée Grévin",
    address: ["Passage Jouffroy", "75009 Paris"],
    detail: "Disponible à la boutique du musée",
  },
]

const previewPages = buildPreviewPages()

const LivrePage = () => {
  const [activePreviewIndex, setActivePreviewIndex] = React.useState(null)

  React.useEffect(() => {
    if (activePreviewIndex === null) {
      return undefined
    }

    const onKeyDown = event => {
      if (event.key === "Escape") {
        setActivePreviewIndex(null)
      }

      if (event.key === "ArrowRight" && previewPages.length > 1) {
        setActivePreviewIndex(current => (current + 1) % previewPages.length)
      }

      if (event.key === "ArrowLeft" && previewPages.length > 1) {
        setActivePreviewIndex(current =>
          current === 0 ? previewPages.length - 1 : current - 1
        )
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [activePreviewIndex])

  const openPreview = index => {
    setActivePreviewIndex(index)
  }

  const closePreview = () => {
    setActivePreviewIndex(null)
  }

  const showPreviousPreview = event => {
    event.stopPropagation()
    setActivePreviewIndex(current =>
      current === 0 ? previewPages.length - 1 : current - 1
    )
  }

  const showNextPreview = event => {
    event.stopPropagation()
    setActivePreviewIndex(current => (current + 1) % previewPages.length)
  }

  return (
    <Layout>
      <Helmet>
        <title>Livre sur l'œuvre d'Alfred Grévin | Galerie Alfred Grévin</title>
        <meta
          name="description"
          content="Découvrez le livre consacré à l'œuvre d'Alfred Grévin : 120 pages richement illustrées, éditées par la SAHT, avec une édition spéciale limitée."
        />
      </Helmet>

      <div className={bookCss.page}>
        <section className={bookCss.hero}>
          <div className={bookCss.heroContent}>
            <p className={bookCss.eyebrow}>Livre événement</p>
            <h1>Le livre de référence sur l'œuvre d'Alfred Grévin</h1>
            <p className={bookCss.subtitle}>
              Quelques mois avant le bicentenaire de sa naissance, paraît un
              ouvrage inédit consacré à Alfred Grévin (1827-1892), pour mesurer
              l'ampleur de son talent et la richesse de son œuvre.
            </p>

            {/* <div className={bookCss.actions}>
              <a className={bookCss.primaryButton} href="#points-de-vente">
                Où acheter le livre
              </a>
               <a className={bookCss.secondaryButton} href="#edition-speciale">
                Découvrir l'édition spéciale
              </a> 
            </div> */}

            <p className={bookCss.description}>
              120 pages avec de très nombreuses illustrations pour découvrir ce
              grand artiste du XIXe siècle, de ses dessins humoristiques à ses
              créations pour le Musée Grévin.
            </p>

            <div className={bookCss.proofStrip}>
              <div className={bookCss.proofItem}>
                <strong>120</strong>
                <span>pages illustrées</span>
              </div>
              <div className={bookCss.proofItem}>
                <strong>22 €</strong>
                <span>édition normale</span>
              </div>
            </div>
          </div>

          <div className={bookCss.heroVisual}>
            <div className={bookCss.visualCard}>
              <span className={bookCss.availability}>Disponible</span>
              <img
                src={couverture}
                alt="Couverture du livre sur l'œuvre d'Alfred Grévin"
              />
              <div className={bookCss.visualMeta}>
                <p>Société d'Archéologie et d'Histoire du Tonnerrois</p>
                <div className={bookCss.priceLine}>
                  <span>Édition normale</span>
                  <strong>22 €</strong>
                </div>
                <p>
                  Disponible à Tonnerre et à la boutique du Musée Grévin à
                  Paris.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={bookCss.section}>
          <div className={bookCss.sectionHeader}>
            <span>Ce que le livre révèle</span>
            <h2>
              Un artiste bien plus vaste que l'image que l'on connaît de lui
            </h2>
            <p>
              Alfred Grévin fut dessinateur, illustrateur, créateur pour le
              théâtre, sculpteur et inventeur d'images. Cet ouvrage propose une
              lecture complète de son parcours et montre combien son œuvre
              déborde les catégories habituelles.
            </p>
          </div>

          <div className={bookCss.featureGrid}>
            {features.map(feature => {
              return (
                <article className={bookCss.featureCard} key={feature.title}>
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                </article>
              )
            })}
          </div>
        </section>

        <section className={bookCss.section}>
          <div className={bookCss.splitGrid}>
            <article className={bookCss.storyCard}>
              <span className={bookCss.cardEyebrow}>Pourquoi ce livre</span>
              <h2>Une parution pensée pour le bicentenaire</h2>
              <p>
                À l'approche des 200 ans de la naissance d'Alfred Grévin, ce
                livre vient combler un manque en réunissant en un seul volume
                les facettes majeures de son travail.
              </p>
              <p>
                Édité par la Société d'Archéologie et d'Histoire du Tonnerrois
                (SAHT), il offre une synthèse accessible, documentée et très
                illustrée, conçue pour donner envie de regarder à nouveau un
                artiste essentiel du XIXe siècle.
              </p>
            </article>

            <article className={bookCss.readersCard}>
              <span className={bookCss.cardEyebrow}>Pour qui</span>
              <h2>Un livre à lire, offrir et garder</h2>
              <ul className={bookCss.readerList}>
                {readers.map(reader => {
                  return <li key={reader}>{reader}</li>
                })}
              </ul>
            </article>
          </div>
        </section>

        {previewPages.length > 0 && (
          <section className={`${bookCss.section} ${bookCss.previewSection}`}>
            <div className={bookCss.sectionHeader}>
              <span>Feuilleter le livre</span>
              <h2>Quelques pages pour entrer dans l'univers du livre</h2>
              <p>
                Une sélection de pages permettra de retrouver le ton, la
                richesse des illustrations et l'élégance éditoriale de
                l'ouvrage. Cliquez sur une page pour l'afficher en grand.
              </p>
            </div>

            <div className={bookCss.previewGrid}>
              {previewPages.map((page, index) => {
                return (
                  <button
                    key={page.label}
                    type="button"
                    className={bookCss.previewCard}
                    onClick={() => openPreview(index)}
                  >
                    <span className={bookCss.previewFrame}>
                      <img src={page.src} alt={page.alt} />
                    </span>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        <section className={bookCss.section} id="points-de-vente">
          <div className={bookCss.sectionHeader}>
            <span>Points de vente</span>
            <h2>Où trouver le livre dès maintenant</h2>
            <p>
              Le livre est édité par la Société d'Archéologie et d'Histoire du
              Tonnerrois (SAHT) et disponible dans plusieurs lieux choisis entre
              Tonnerre et Paris.
            </p>
          </div>

          <div className={bookCss.locationGrid}>
            {locations.map(location => {
              return (
                <article className={bookCss.locationCard} key={location.name}>
                  <h3>{location.name}</h3>
                  <div className={bookCss.addressBlock}>
                    {location.address.map(line => {
                      return <p key={line}>{line}</p>
                    })}
                  </div>
                  <p className={bookCss.locationDetail}>{location.detail}</p>
                  {location.href && (
                    <a href={location.href}>{location.linkLabel}</a>
                  )}
                </article>
              )
            })}
          </div>
        </section>

        <section
          className={`${bookCss.section} ${bookCss.specialSection}`}
          id="edition-speciale"
        >
          <div className={bookCss.specialIntro}>
            <span>Édition spéciale</span>
            <h2>Un tirage cartonné, enrichi et strictement limité</h2>
            <p>
              Un tirage spécial réalisé pour l'association Galerie Alfred Grévin
              est disponible. Cette version cartonnée comporte 6 pages
              supplémentaires et s'adresse aux lecteurs qui souhaitent une
              édition rare.
            </p>
          </div>

          <div className={bookCss.specialGrid}>
            <article className={bookCss.specialCard}>
              <h3>Ce que comprend cette édition</h3>
              <ul className={bookCss.specialList}>
                <li>Une fabrication cartonnée</li>
                <li>6 pages supplémentaires</li>
                <li>Un tirage limité à 60 exemplaires</li>
                <li>50 exemplaires numérotés et 10 réservés à l'auteur</li>
                <li>Prix : 55 € (+ port)</li>
              </ul>
            </article>

            <article className={bookCss.specialContact}>
              <h3>Association Galerie Alfred Grévin</h3>
              <p>33 rue de l'hôpital</p>
              <p>89700 Tonnerre</p>
              <a
                href="https://www.alfredgrevin.com"
                target="_blank"
                rel="noreferrer"
              >
                www.alfredgrevin.com
              </a>
              <p>
                Pour commander le livre, nous contacter par email:
                lefvakana@gmail.com
              </p>
            </article>
          </div>
        </section>

        {/* <section className={bookCss.finalCallout}>
          <div>
            <span className={bookCss.finalEyebrow}>Passer à l'action</span>
            <h2>
              Commencez par l'édition normale, ou réservez la version limitée
            </h2>
            <p>
              L'édition normale est disponible dès maintenant à 22 €. Pour le
              tirage spécial cartonné, consultez le site de l'association
              Galerie Alfred Grévin.
            </p>
          </div>

          <div className={bookCss.finalActions}>
            <a className={bookCss.primaryButtonLight} href="#points-de-vente">
              Choisir un point de vente
            </a>
            <a
              className={bookCss.secondaryButtonLight}
              href="https://www.alfredgrevin.com"
              target="_blank"
              rel="noreferrer"
            >
              Visiter le site
            </a>
          </div>
        </section> */}
      </div>

      {activePreviewIndex !== null && (
        <div
          className={bookCss.lightbox}
          role="dialog"
          aria-modal="true"
          aria-label={previewPages[activePreviewIndex].alt}
          onClick={closePreview}
        >
          <button
            type="button"
            className={bookCss.lightboxClose}
            onClick={closePreview}
          >
            Fermer
          </button>

          {previewPages.length > 1 && (
            <button
              type="button"
              className={`${bookCss.lightboxNav} ${bookCss.lightboxPrev}`}
              onClick={showPreviousPreview}
              aria-label="Voir la page précédente"
            >
              ←
            </button>
          )}

          <div
            className={bookCss.lightboxInner}
            onClick={event => event.stopPropagation()}
          >
            <div className={bookCss.lightboxPaper}>
              <img
                src={previewPages[activePreviewIndex].src}
                alt={previewPages[activePreviewIndex].alt}
              />
            </div>
            <div className={bookCss.lightboxMeta}>
              <strong>{previewPages[activePreviewIndex].label}</strong>
              <span>
                {activePreviewIndex + 1} / {previewPages.length}
              </span>
            </div>
          </div>

          {previewPages.length > 1 && (
            <button
              type="button"
              className={`${bookCss.lightboxNav} ${bookCss.lightboxNext}`}
              onClick={showNextPreview}
              aria-label="Voir la page suivante"
            >
              →
            </button>
          )}
        </div>
      )}
    </Layout>
  )
}

export default LivrePage
